import cn from 'classnames';
import React, { JSX, useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type { Difficulty, OverallRating } from '@local-types/library/object';

import { useAnchoredPosition } from '@hooks/library/useAnchoredPosition';
import { useClickOutside } from '@hooks/library/useClickOutside';
import { usePresence } from '@hooks/library/usePresence';

import { DIFFICULTY_META, OVERALL_COLORS } from '@lib/library/objectMeta';

import { ArrowIcon } from '@icons/library/svg';

import { Text, TypographyVariant } from '@components/library/atoms/Text';

import type { RatingBoxProps } from './RatingBox.types';

import styles from './RatingBox.module.scss';

const OVERALL_VALUES: OverallRating[] = [1, 2, 3, 4, 5];
const DIFFICULTY_VALUES: Difficulty[] = [
  'very_hard',
  'hard',
  'moderate',
  'easy',
];

interface ColoredSelectProps<T extends string | number> {
  label: string;
  value?: T | null;
  options: T[];
  renderLabel: (value: T) => string;
  getColor: (value: T) => string;
  onChange?: (value: T | null) => void;
  readOnly: boolean;
  placeholder: string;
  valueSuffix?: string;
}

function ColoredSelect<T extends string | number>(
  props: ColoredSelectProps<T>,
): JSX.Element {
  const {
    label,
    value,
    options,
    renderLabel,
    getColor,
    onChange,
    readOnly,
    placeholder,
    valueSuffix,
  } = props;
  const [isOpen, setIsOpen] = useState(false);

  const close = useCallback(() => setIsOpen(false), []);
  const ref = useClickOutside(close);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Keep the portaled menu glued to the trigger as the modal/page scrolls.
  // Placement is decided by viewport width, not measured space: open upward at
  // 1920px and below, downward only on wider screens. Width-based placement is
  // settled before the menu paints, so it never opens one way then jumps.
  const menuPos = useAnchoredPosition(triggerRef, isOpen, menuRef, {
    openUpMaxWidth: 1920,
  });

  // The menu stays mounted for its fade-out.
  const { mounted: menuMounted, shown: menuShown } = usePresence(isOpen, 120);

  const handleToggle = () => {
    if (readOnly) return;
    setIsOpen(prev => !prev);
  };

  const handleSelect = (next: T | null) => {
    onChange?.(next);
    setIsOpen(false);
  };

  const hasValue = value !== undefined && value !== null;
  const displayLabel = hasValue ? renderLabel(value as T) : placeholder;
  const displayColor = hasValue ? getColor(value as T) : undefined;

  const menu =
    menuMounted && !readOnly && menuPos && typeof document !== 'undefined'
      ? createPortal(
          <div className="library">
            <div
              ref={menuRef}
              role="listbox"
              className={cn(styles.menu, {
                [styles.menuClosing]: !menuShown,
              })}
              style={{
                position: 'fixed',
                top: menuPos.top,
                left: menuPos.left,
                width: menuPos.width,
                zIndex: 1500,
                transform:
                  menuPos.placement === 'top' ? 'translateY(-100%)' : undefined,
              }}
              // Keep clicks inside the portaled menu from triggering useClickOutside.
              onPointerDown={e => e.stopPropagation()}
            >
              <button
                type="button"
                role="option"
                aria-selected={!hasValue}
                className={cn(styles.option, { [styles.selected]: !hasValue })}
                onClick={() => handleSelect(null)}
              >
                Not rated
              </button>
              {options.map(opt => (
                <button
                  key={String(opt)}
                  type="button"
                  role="option"
                  aria-selected={opt === value}
                  className={cn(styles.option, {
                    [styles.selected]: opt === value,
                  })}
                  onClick={() => handleSelect(opt)}
                  style={{ color: getColor(opt) }}
                >
                  {renderLabel(opt)}
                </button>
              ))}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={ref} className={styles.field}>
      <Text variant={TypographyVariant.TextSmall} className={styles.label}>
        {label}
      </Text>

      <button
        ref={triggerRef}
        type="button"
        className={cn(styles.trigger, {
          [styles.open]: isOpen,
          [styles.readOnly]: readOnly,
        })}
        onClick={handleToggle}
        aria-haspopup={readOnly ? undefined : 'listbox'}
        aria-expanded={readOnly ? undefined : isOpen}
        aria-label={label}
        // Read-only is a fact, not a switched-off control: it is never
        // disabled, so a visitor's unrated book does not read as broken.
        tabIndex={readOnly ? -1 : undefined}
      >
        <Text
          variant={TypographyVariant.TextBase}
          className={cn(styles.value, {
            [styles.placeholder]: !hasValue,
          })}
        >
          <span style={displayColor ? { color: displayColor } : undefined}>
            {displayLabel}
          </span>
          {hasValue && valueSuffix && (
            <span className={styles.suffix}>{valueSuffix}</span>
          )}
        </Text>
        {!readOnly && (
          <ArrowIcon
            width={12}
            height={12}
            className={cn(styles.chevron, { [styles.rotated]: isOpen })}
          />
        )}
      </button>

      {menu}
    </div>
  );
}

export function RatingBox(props: RatingBoxProps): JSX.Element {
  const {
    username,
    overallRating,
    difficulty,
    onOverallChange,
    onDifficultyChange,
    readOnly = false,
    itemLabel = 'book',
    className,
  } = props;

  // The header states the fact before the values do: an unrated item says so
  // outright instead of leaving two dashes to carry the meaning.
  // Strapi returns an unset rating as null, not undefined; either is unrated.
  const isRated = overallRating != null || difficulty != null;
  const header = username
    ? `${username} ${isRated ? `rated this ${itemLabel}:` : `didn’t rate this ${itemLabel}`}`
    : isRated
      ? 'Rating:'
      : 'Not rated yet';

  return (
    <div className={cn(styles.wrapper, className)}>
      <Text variant={TypographyVariant.TextSmall} className={styles.header}>
        {header}
      </Text>
      {/* A visitor to an unrated item gets the sentence and nothing else: two
          read-only dashes under "rated" read as a rating of nothing. */}
      {(isRated || !readOnly) && (
        <div className={styles.row}>
          <ColoredSelect<OverallRating>
            label="Overall:"
            value={overallRating}
            options={OVERALL_VALUES}
            renderLabel={v => String(v)}
            getColor={v => OVERALL_COLORS[v]}
            onChange={onOverallChange}
            readOnly={readOnly}
            placeholder="—"
            valueSuffix="/5"
          />
          <ColoredSelect<Difficulty>
            label="Difficulty:"
            value={difficulty}
            options={DIFFICULTY_VALUES}
            renderLabel={v => DIFFICULTY_META[v].label}
            getColor={v => DIFFICULTY_META[v].color}
            onChange={onDifficultyChange}
            readOnly={readOnly}
            placeholder="—"
          />
        </div>
      )}
    </div>
  );
}
